import type { User } from 'firebase/auth';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { useStudentView } from '@/features/studentView/StudentViewProvider';
import type { NormalizedAssistantInfo } from '@/features/chat/chatTypes';
import {
  getComponentTimeline,
  type ComponentTimeline,
} from '@/features/content/hooks/useComponentTimeline';
import type { RawComponent as ContentRawComponent } from '@/features/content/useComponent';
import type {
  GradebookAssignment,
  GradebookQuestionMeta,
  GradebookSectionMeta,
} from '@/features/gradebook/gradebookTypes';
import {
  coachDueEntries,
  coachVisibleMaxScore,
  isThreadClosed,
  isThreadOpen,
  normalizeCoachSection,
} from '@/features/kaiCoach/kaiCoachContent';
import { getQuestionPrompt, getQuestionTypeLabel } from '@/features/gradebook/questionMetaText';
import { getKaiCoachSection } from '@/features/kaiCoach/kaiCoachSection';

import type { CoachDueSoonLine, DueSoonLine } from './courseHomeUtils';

export interface RawDateMessage {
  day?: number | null;
  month?: number | null;
  year?: number | null;
}

interface RawUserProfile {
  display_name?: string | null;
  displayName?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  last_name?: string | null;
  lastName?: string | null;
  name?: string | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
  preferred_username?: string | null;
  preferredUsername?: string | null;
}

interface RawUserMessage extends RawUserProfile {
  email?: string | null;
  id?: string | null;
  profile?: RawUserProfile | null;
  uid?: string | null;
}

interface RawAssistantInfo {
  id?: string | null;
  provider?: number | string | null;
  thread_id?: string | null;
  threadId?: string | null;
}

export interface CourseSettings {
  disable_auto_numbering?: boolean | null;
  disable_due_soon_section?: boolean | null;
  disableAutoNumbering?: boolean | null;
  disableDueSoonSection?: boolean | null;
  // A course-wide override for how large images are DISPLAYED. Never changes
  // what any image stores. See courseDisplaySettings.ts for how it resolves —
  // the values arrive as numbers or as enum names depending on the path.
  image_size_override?: number | string | null;
  imageSizeOverride?: number | string | null;
  image_size_override_includes_questions?: boolean | null;
  imageSizeOverrideIncludesQuestions?: boolean | null;
  [key: string]: unknown;
}

export interface RawQuestionSettings {
  allow_ai_assistant?: number | string | null;
  allowAiAssistant?: number | string | null;
  allow_ai_hints?: number | string | null;
  allowAiHints?: number | string | null;
  // Independent of allow_ai_hints. Unset means "follow allow_ai_hints" so that
  // chapters authored before the settings were split keep their behavior.
  allow_ai_practice_problems?: number | string | null;
  allowAiPracticeProblems?: number | string | null;
  // Attempts before an AI hint is offered. Unset or <= 0 means immediately.
  // Independent of num_attempts_to_show_hints_* (the authored-hint threshold).
  num_attempts_to_show_ai_hints?: number | string | null;
  numAttemptsToShowAiHints?: number | string | null;
  number_of_questions?: number | string | null;
  numberOfQuestions?: number | string | null;
  num_attempts_exact_answer?: number | string | null;
  numAttemptsExactAnswer?: number | string | null;
  num_attempts_multiple_choice?: number | string | null;
  numAttemptsMultipleChoice?: number | string | null;
  num_attempts_to_show_hints_exact_answer?: number | string | null;
  numAttemptsToShowHintsExactAnswer?: number | string | null;
  num_attempts_to_show_hints_multiple_choice?: number | string | null;
  numAttemptsToShowHintsMultipleChoice?: number | string | null;
  show_practice_solutions_setting?: number | string | null;
  showPracticeSolutionsSetting?: number | string | null;
  show_solutions_after_interval?: number | string | null;
  showSolutionsAfterInterval?: number | string | null;
  type?: number | string | null;
  user_defined_numbers?: Array<number | string> | null;
  userDefinedNumbers?: Array<number | string> | null;
  [key: string]: unknown;
}

interface RawQuestion {
  content?: string | null;
  content_section?: unknown;
  contentSection?: unknown;
  id?: number | string | null;
  question_set?: RawQuestionSet | null;
  questionSet?: RawQuestionSet | null;
  // Removed from the assignment by an instructor (SetQuestionRemoved).
  removed?: boolean | null;
  type?: number | string | null;
  uid?: number | string | null;
  value?: number | string | null;
  [key: string]: unknown;
}

interface RawQuestionSet {
  questions?: RawQuestion[] | null;
  questionsList?: RawQuestion[] | null;
  total_score?: number | string | null;
  totalScore?: number | string | null;
}

interface RawQuestionSection {
  questions?: RawQuestion[] | null;
  questionsList?: RawQuestion[] | null;
}

interface RawSection {
  id?: number | string | null;
  kai_coach_section?:
    | import('@/features/kaiCoach/kaiCoachTypes').RawKaiCoachSection
    | null;
  kaiCoachSection?:
    | import('@/features/kaiCoach/kaiCoachTypes').RawKaiCoachSection
    | null;
  pre_lecture_question_section?: RawQuestionSection | null;
  preLectureQuestionSection?: RawQuestionSection | null;
  question_section?: RawQuestionSection | null;
  questionSection?: RawQuestionSection | null;
  uid?: number | string | null;
}

export interface RawComponent {
  content_start_time_utc_milli?: string | null;
  contentStartTimeUtcMilli?: string | null;
  course_question_settings?: RawQuestionSettings | null;
  courseQuestionSettings?: RawQuestionSettings | null;
  end_date?: RawDateMessage | null;
  end_time_utc_milli?: string | null;
  endDate?: RawDateMessage | null;
  endTimeUtcMilli?: string | null;
  id?: string | null;
  is_complete?: boolean | null;
  is_live?: boolean | null;
  is_pre_lecture_complete?: boolean | null;
  is_pre_lecture_live?: boolean | null;
  is_section_divider?: boolean | null;
  isComplete?: boolean | null;
  isLive?: boolean | null;
  isPreLectureComplete?: boolean | null;
  isPreLectureLive?: boolean | null;
  isSectionDivider?: boolean | null;
  last_updated_time_seconds?: string | number | null;
  lastUpdatedTimeSeconds?: string | number | null;
  pre_lecture_end_time_utc_milli?: string | null;
  pre_lecture_question_settings?: RawQuestionSettings | null;
  pre_lecture_start_time_utc_milli?: string | null;
  preLectureEndTimeUtcMilli?: string | null;
  preLectureQuestionSettings?: RawQuestionSettings | null;
  preLectureStartTimeUtcMilli?: string | null;
  question_settings?: RawQuestionSettings | null;
  questionSettings?: RawQuestionSettings | null;
  section_stats?: unknown[] | null;
  sectionStats?: unknown[] | null;
  sections?: RawSection[] | null;
  start_date?: RawDateMessage | null;
  start_time_utc_milli?: string | null;
  startDate?: RawDateMessage | null;
  startTimeUtcMilli?: string | null;
  title?: string | null;
  uid?: string | null;
}

export interface RawCourse {
  assistant_info?: RawAssistantInfo | null;
  assistantInfo?: RawAssistantInfo | null;
  assistant_ids?: string[] | null;
  assistantIds?: string[] | null;
  assistants?: RawUserMessage[] | null;
  component_uids?: string[] | null;
  componentUids?: string[] | null;
  components?: RawComponent[] | null;
  course_prelecture_question_settings?: RawQuestionSettings | null;
  coursePrelectureQuestionSettings?: RawQuestionSettings | null;
  course_question_settings?: RawQuestionSettings | null;
  courseQuestionSettings?: RawQuestionSettings | null;
  course_settings?: CourseSettings | null;
  courseSettings?: CourseSettings | null;
  custom_component_order?: boolean | null;
  customComponentOrder?: boolean | null;
  end_date?: RawDateMessage | null;
  endDate?: RawDateMessage | null;
  id?: string | null;
  instructor?: RawUserMessage | null;
  instructor_id?: string | null;
  instructorId?: string | null;
  is_complete?: boolean | null;
  is_live?: boolean | null;
  isComplete?: boolean | null;
  isLive?: boolean | null;
  lectures?: unknown[] | null;
  name?: string | null;
  parent_subject?: string | null;
  parentSubject?: string | null;
  secondary_instructor_ids?: string[] | null;
  secondary_instructors?: RawUserMessage[] | null;
  secondaryInstructorIds?: string[] | null;
  secondaryInstructors?: RawUserMessage[] | null;
  student_ids?: string[] | null;
  studentIds?: string[] | null;
  students?: RawUserMessage[] | null;
  start_date?: RawDateMessage | null;
  startDate?: RawDateMessage | null;
  uid?: string | null;
}

export interface RawGetCoursePayload {
  course?: RawCourse | null;
}

export interface RawGetComponentPayload {
  component?: RawComponent | null;
}

interface GetCourseRequest {
  course_id: string;
  full_response: boolean;
}

interface GetComponentRequest {
  component_id: string;
  course_id: string;
}

type KuduIdentityUser = Pick<User, 'email' | 'providerData' | 'uid'>;

export type UserRole = 'instructor' | 'assistant' | 'student';

export interface CourseStaffMember {
  email?: string;
  id: string;
  name: string;
}

export interface CourseHomepage {
  assistantInfo?: NormalizedAssistantInfo;
  assistantIds?: string[];
  assistants?: CourseStaffMember[];
  componentUids: string[];
  components: ComponentListItem[];
  coursePrelectureQuestionSettings?: RawQuestionSettings;
  courseQuestionSettings?: RawQuestionSettings;
  courseSettings: CourseSettings;
  customComponentOrder: boolean;
  gradebookAssignments?: GradebookAssignment[];
  // Student role only: how many components visibility settings currently hide
  // (drives student view's "show all content" affordance).
  hiddenComponentCount?: number;
  id: string;
  // Scoped identity (id + name + email) for the primary instructor, when the
  // API supplies it. `instructorName` remains the always-present display string.
  instructor?: CourseStaffMember;
  // The primary instructor's user id: only they can remove staff.
  instructorId?: string;
  instructorName: string;
  isComplete?: boolean;
  isInstructorView: boolean;
  isLive?: boolean;
  parentSubject?: string;
  secondaryInstructorIds?: string[];
  secondaryInstructors?: CourseStaffMember[];
  studentCount?: number;
  termLabel: string;
  title: string;
  userRole: UserRole;
}

export type ComponentListItem = SectionDividerItem | CourseComponentItem;

export interface SectionDividerItem {
  // A divider IS a component with is_section_divider set, so it carries the same
  // delete keys. They are resolved exactly like a chapter's — see the
  // componentUidSet note below; an id the course's component_uids list does not
  // contain deletes nothing.
  componentId?: string;
  componentUid?: string;
  id: string;
  kind: 'divider';
  title: string;
}

export interface CourseComponentItem {
  componentId?: string;
  componentUid?: string;
  dateLabel?: string;
  dueLabel?: string;
  dueSoonLines?: DueSoonLine[];
  editHref?: string;
  href: string;
  id: string;
  isComplete?: boolean;
  isLive?: boolean;
  isPreLectureComplete?: boolean;
  isPreLectureLive?: boolean;
  kind: 'component';
  preLectureLabel?: string;
  rawComponent?: RawComponent;
  scoreLabel?: string;
  statusLabel: 'Closed' | 'Draft' | 'Live' | 'Scheduled';
  title: string;
  visibilityLabel?: string;
}

interface NormalizeCourseOptions {
  courseId: string;
  currentUser?: KuduIdentityUser | null;
  // Student view "show hidden content": include components that visibility
  // settings hide from students, labeled as hidden. Display-only — no setting
  // is changed, and the default (false) stays the accurate student experience.
  includeHiddenComponentsForStudent?: boolean;
}

export const courseQueryKey = (courseId: string) =>
  ['course', courseId] as const;

function createDataError(message: string, status?: number): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  error.status = status;
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function toComponentArray(value: unknown): RawComponent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawComponent[];
}

function toRecordArray<T>(value: unknown): T[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as T[];
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeString(entry))
    .filter((entry): entry is string => entry !== null);
}

function toUserArray(value: unknown): RawUserMessage[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawUserMessage[];
}

function getDateMessage(value: unknown): RawDateMessage | null {
  return isRecord(value) ? (value as RawDateMessage) : null;
}

function getDateLabel(date?: RawDateMessage | null): string | null {
  if (!date?.year || !date?.month) {
    return null;
  }

  const safeDay = date.day && date.day > 0 ? date.day : 1;
  const formatted = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(new Date(Date.UTC(date.year, date.month - 1, safeDay)));

  return formatted;
}

function getDateTimeLabel(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function getDayLabel(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

type QuestionAssignmentType = 'ALL' | 'FIXED' | 'RANDOM' | 'USER_DEFINED';

const QUESTION_ASSIGNMENT_TYPE_MAP: Record<string, QuestionAssignmentType> = {
  '0': 'ALL',
  '1': 'ALL',
  '2': 'FIXED',
  '3': 'RANDOM',
  '4': 'USER_DEFINED',
  ALL: 'ALL',
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  UNKNOWN: 'ALL',
  USER_DEFINED: 'USER_DEFINED',
};

function normalizeQuestionAssignmentType(value: unknown): QuestionAssignmentType {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return QUESTION_ASSIGNMENT_TYPE_MAP[String(value)] ?? 'ALL';
  }

  const normalized = normalizeString(value)?.toUpperCase();
  return normalized ? (QUESTION_ASSIGNMENT_TYPE_MAP[normalized] ?? 'ALL') : 'ALL';
}

export function deriveTermLabel(date?: RawDateMessage | null): string {
  if (!date?.year || !date?.month) {
    return 'Term TBD';
  }

  let season = 'Term TBD';

  if (date.month >= 1 && date.month <= 3) {
    season = 'Winter';
  } else if (date.month >= 4 && date.month <= 5) {
    season = 'Spring';
  } else if (date.month >= 6 && date.month <= 8) {
    season = 'Summer';
  } else if (date.month >= 9 && date.month <= 12) {
    season = 'Fall';
  }

  return season === 'Term TBD' ? season : `${season} ${date.year}`;
}

function getProfile(source: RawUserMessage | null | undefined) {
  return isRecord(source?.profile) ? (source.profile as RawUserProfile) : null;
}

function getUserField(
  user: RawUserMessage | null | undefined,
  snakeKey: keyof RawUserProfile,
  camelKey: keyof RawUserProfile,
): string | null {
  const profile = getProfile(user);

  return (
    normalizeString(user?.[snakeKey]) ??
    normalizeString(user?.[camelKey]) ??
    normalizeString(profile?.[snakeKey]) ??
    normalizeString(profile?.[camelKey])
  );
}

function getUserDisplayName(user: RawUserMessage | null | undefined): string | null {
  const preferredUsername = getUserField(
    user,
    'preferred_username',
    'preferredUsername',
  );

  if (preferredUsername) {
    return preferredUsername;
  }

  const firstName =
    getUserField(user, 'first_name', 'firstName') ??
    normalizeString(user?.first_name) ??
    normalizeString(user?.firstName);
  const lastName =
    getUserField(user, 'last_name', 'lastName') ??
    normalizeString(user?.last_name) ??
    normalizeString(user?.lastName);

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return (
    firstName ??
    lastName ??
    getUserField(user, 'preferred_email', 'preferredEmail') ??
    getUserField(user, 'name', 'displayName') ??
    getUserField(user, 'display_name', 'displayName') ??
    normalizeString(user?.email)
  );
}

function getUserId(user: RawUserMessage | null | undefined): string | null {
  return (
    normalizeString(user?.id) ??
    normalizeString(user?.uid) ??
    getUserField(user, 'preferred_email', 'preferredEmail') ??
    normalizeString(user?.email)
  );
}

function getUserEmail(user: RawUserMessage | null | undefined): string | null {
  return (
    getUserField(user, 'preferred_email', 'preferredEmail') ??
    normalizeString(user?.email)
  );
}

function normalizeStaffMembers(
  ids: string[],
  users: RawUserMessage[],
): CourseStaffMember[] {
  const members = new Map<string, CourseStaffMember>();

  for (const user of users) {
    const id = getUserId(user);

    if (!id) {
      continue;
    }

    const email = getUserEmail(user);

    members.set(id, {
      ...(email ? { email } : {}),
      id,
      name: getUserDisplayName(user) ?? email ?? id,
    });
  }

  for (const id of ids) {
    if (members.has(id)) {
      continue;
    }

    members.set(id, {
      id,
      name: id,
    });
  }

  return [...members.values()];
}

// The scoped identity (id + display name + email) for a single staff user, e.g.
// the primary instructor. Returns undefined when the user carries no usable id.
function normalizeStaffMember(
  user: RawUserMessage | null | undefined,
): CourseStaffMember | undefined {
  const id = getUserId(user);

  if (!id) {
    return undefined;
  }

  const email = getUserEmail(user);

  return {
    ...(email ? { email } : {}),
    id,
    name: getUserDisplayName(user) ?? email ?? id,
  };
}

// Every identifier a staff object can be matched against: its id, email, and
// username. Unlike a profile-only match, this also covers the object's `id`/
// `email` so a user is recognized whether the backend lists them by id array or
// only as a populated staff object.
function getStaffMatchTokens(user: RawUserMessage | null | undefined): string[] {
  return [
    getUserId(user),
    getUserEmail(user),
    getUserField(user, 'preferred_username', 'preferredUsername'),
  ]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase());
}

function collectStaffMatchTokens(
  ids: Array<string | null>,
  users: RawUserMessage[],
): Set<string> {
  const tokens = new Set<string>();

  for (const id of ids) {
    const normalized = normalizeString(id);

    if (normalized) {
      tokens.add(normalized.toLowerCase());
    }
  }

  for (const user of users) {
    for (const token of getStaffMatchTokens(user)) {
      tokens.add(token);
    }
  }

  return tokens;
}

function getCurrentUserMatchValues(
  currentUser: KuduIdentityUser | null | undefined,
): string[] {
  const values = new Set<string>();
  const kuduUserId = normalizeString(getKuduUserId(currentUser));

  if (kuduUserId) {
    values.add(kuduUserId.toLowerCase());
  }

  const email = normalizeString(currentUser?.email);

  if (email) {
    values.add(email.toLowerCase());
  }

  for (const providerInfo of currentUser?.providerData ?? []) {
    const providerUid = normalizeString(providerInfo.uid);
    const providerEmail = normalizeString(providerInfo.email);

    if (providerUid) {
      values.add(providerUid.toLowerCase());
    }

    if (providerEmail) {
      values.add(providerEmail.toLowerCase());
    }
  }

  return [...values];
}

export function deriveIsInstructorView(
  course: RawCourse,
  currentUser?: KuduIdentityUser | null,
): boolean {
  return deriveUserRole(course, currentUser) !== 'student';
}

export function deriveUserRole(
  course: RawCourse,
  currentUser?: KuduIdentityUser | null,
): UserRole {
  const currentUserMatchValues = getCurrentUserMatchValues(currentUser);

  if (currentUserMatchValues.length === 0) {
    return 'student';
  }

  // Instructors — primary and secondary — all have full edit rights. Match the
  // current user against every available identifier (the id arrays as well as
  // the id/email/username on each staff object) so secondary instructors are
  // recognized whether the backend sends them as ids or as populated objects.
  const instructorTokens = collectStaffMatchTokens(
    [
      normalizeString(course.instructor_id ?? course.instructorId),
      ...toStringArray(
        course.secondary_instructor_ids ?? course.secondaryInstructorIds,
      ),
    ],
    [
      ...(course.instructor ? [course.instructor] : []),
      ...toUserArray(course.secondary_instructors ?? course.secondaryInstructors),
    ],
  );

  if (currentUserMatchValues.some((value) => instructorTokens.has(value))) {
    return 'instructor';
  }

  // Assistants can view answers and class-level stats but cannot edit the
  // course, so they are matched only after instructors.
  const assistantTokens = collectStaffMatchTokens(
    toStringArray(course.assistant_ids ?? course.assistantIds),
    toUserArray(course.assistants),
  );

  if (currentUserMatchValues.some((value) => assistantTokens.has(value))) {
    return 'assistant';
  }

  return 'student';
}

export function orderCourseComponents(course: RawCourse): RawComponent[] {
  const components = toComponentArray(course.components);
  const shouldUseCustomOrder =
    normalizeBoolean(
      course.custom_component_order ?? course.customComponentOrder,
    ) ?? false;

  if (!shouldUseCustomOrder) {
    return components;
  }

  const orderedUids = toStringArray(course.component_uids ?? course.componentUids);

  if (orderedUids.length === 0) {
    return components;
  }

  const remainingComponents = [...components];
  const orderedComponents: RawComponent[] = [];

  for (const orderedUid of orderedUids) {
    const componentIndex = remainingComponents.findIndex((component) => {
      const componentUid = normalizeString(component.uid);
      const componentId = normalizeString(component.id);

      return componentUid === orderedUid || componentId === orderedUid;
    });

    if (componentIndex >= 0) {
      orderedComponents.push(...remainingComponents.splice(componentIndex, 1));
    }
  }

  return [...orderedComponents, ...remainingComponents];
}

function getCourseSettings(course: RawCourse): CourseSettings {
  const settings = course.course_settings ?? course.courseSettings;
  return isRecord(settings) ? { ...settings } : {};
}

function getCourseQuestionSettings(course: RawCourse): RawQuestionSettings | undefined {
  const settings = course.course_question_settings ?? course.courseQuestionSettings;
  return isRecord(settings) ? { ...(settings as RawQuestionSettings) } : undefined;
}

function getCoursePrelectureQuestionSettings(course: RawCourse): RawQuestionSettings | undefined {
  const settings =
    course.course_prelecture_question_settings ??
    course.coursePrelectureQuestionSettings;
  return isRecord(settings) ? { ...(settings as RawQuestionSettings) } : undefined;
}

function getAssistantInfo(course: RawCourse): NormalizedAssistantInfo | undefined {
  const rawInfo = course.assistant_info ?? course.assistantInfo;

  if (!isRecord(rawInfo)) {
    return undefined;
  }

  const id = normalizeString(rawInfo.id);

  if (!id) {
    return undefined;
  }

  const threadId = normalizeString(rawInfo.thread_id ?? rawInfo.threadId);

  return {
    id,
    provider: normalizeNumber(rawInfo.provider) ?? 0,
    ...(threadId ? { threadId } : {}),
  };
}

function getStudentCount(course: RawCourse): number {
  const studentIds = toStringArray(course.student_ids ?? course.studentIds);

  if (studentIds.length > 0) {
    return studentIds.length;
  }

  return toUserArray(course.students).length;
}

function getQuestionSettings(
  component: RawComponent,
  kind: 'homework' | 'preLecture',
): RawQuestionSettings | undefined {
  const rawSettings =
    kind === 'preLecture'
      ? component.pre_lecture_question_settings ??
        component.preLectureQuestionSettings
      : component.question_settings ??
        component.questionSettings ??
        component.course_question_settings ??
        component.courseQuestionSettings;

  return isRecord(rawSettings) ? (rawSettings as RawQuestionSettings) : undefined;
}

function getQuestionSetQuestions(question: RawQuestion): RawQuestion[] {
  const questionSet = isRecord(question.question_set ?? question.questionSet)
    ? ((question.question_set ?? question.questionSet) as RawQuestionSet)
    : null;

  return toRecordArray<RawQuestion>(
    questionSet?.questions ?? questionSet?.questionsList,
  );
}

function getQuestionPointsPossible(question: RawQuestion): number {
  const subQuestions = getQuestionSetQuestions(question);

  if (subQuestions.length > 0) {
    return subQuestions.reduce(
      (total, subQuestion) => total + getQuestionPointsPossible(subQuestion),
      0,
    );
  }

  const value = normalizeNumber(question.value);
  return value !== undefined && value > 0 ? value : 1;
}

function createGradebookQuestionMeta(
  question: RawQuestion,
  kind: 'homework' | 'preLecture',
  questionIndex: number,
  sectionId: string,
  sectionIndex: number,
): GradebookQuestionMeta {
  return {
    id:
      normalizeIdentifier(question.id) ??
      normalizeIdentifier(question.uid) ??
      `${sectionId}-${kind}-question-${questionIndex + 1}`,
    index: questionIndex,
    // isAssigned and label are set in a post-pass in getGradebookAssignments,
    // which knows the full component-wide question order the settings
    // reference.
    isAssigned: true,
    isRemoved: normalizeBoolean(question.removed) ?? false,
    kind,
    label: '',
    maxScore: getQuestionPointsPossible(question),
    prompt: getQuestionPrompt(question),
    sectionId,
    sectionIndex,
    typeLabel: getQuestionTypeLabel(question),
  };
}

function getAssignedUserDefinedIndexes(
  settings: RawQuestionSettings | undefined,
  questionCount: number,
): Set<number> {
  const raw = settings?.user_defined_numbers ?? settings?.userDefinedNumbers;

  if (!Array.isArray(raw)) {
    return new Set();
  }

  return new Set(
    raw
      .map((entry) => normalizeNumber(entry))
      .filter((entry): entry is number => entry !== undefined)
      .map((entry) => Math.trunc(entry))
      .filter((entry) => entry >= 0 && entry < questionCount),
  );
}

// The set of display-order indexes (0-based, across the component's sections)
// that are actually assigned, per the component's question settings. This mirrors
// the backend `checkIsAssigned`/scoring so the gradebook shows and totals exactly
// the questions that count:
//   - USER_DEFINED: only the indexes in user_defined_numbers.
//   - FIXED / RANDOM with a configured count: the first N questions.
//   - ALL (or unset/unknown): every question.
// USER_DEFINED settings reference questions by display order across the component's
// sections, so callers must pass questions/scores in that same order.
function getAssignedDisplayIndexes(
  settings: RawQuestionSettings | undefined,
  questionCount: number,
): Set<number> {
  const allIndexes = new Set<number>();
  for (let index = 0; index < questionCount; index += 1) {
    allIndexes.add(index);
  }

  if (questionCount === 0) {
    return allIndexes;
  }

  const assignmentType = normalizeQuestionAssignmentType(settings?.type);

  if (assignmentType === 'USER_DEFINED') {
    return getAssignedUserDefinedIndexes(settings, questionCount);
  }

  if (assignmentType === 'FIXED' || assignmentType === 'RANDOM') {
    const configuredCount = normalizeNumber(
      settings?.number_of_questions ?? settings?.numberOfQuestions,
    );

    if (configuredCount !== undefined && configuredCount > 0) {
      const assignedCount = Math.min(Math.trunc(configuredCount), questionCount);
      const assignedIndexes = new Set<number>();
      for (let index = 0; index < assignedCount; index += 1) {
        assignedIndexes.add(index);
      }
      return assignedIndexes;
    }
  }

  return allIndexes;
}

function getGradebookAssignments(components: RawComponent[]): GradebookAssignment[] {
  return components.map((component, componentIndex) => {
    const componentUid =
      normalizeIdentifier(component.uid) ??
      normalizeIdentifier(component.id) ??
      `component-${componentIndex + 1}`;
    const componentId =
      normalizeIdentifier(component.id) ??
      componentUid;
    const title = normalizeString(component.title) ?? 'Untitled component';
    const isSectionDivider =
      normalizeBoolean(
        component.is_section_divider ?? component.isSectionDivider,
      ) ?? false;

    const sections = toRecordArray<RawSection>(component.sections).map((section, sectionIndex) => {
      const sectionId =
        normalizeIdentifier(section.id) ??
        normalizeIdentifier(section.uid) ??
        `${componentUid}-section-${sectionIndex + 1}`;
      const homeworkSection = isRecord(
        section.question_section ?? section.questionSection,
      )
        ? ((section.question_section ?? section.questionSection) as RawQuestionSection)
        : null;
      const preLectureSection = isRecord(
        section.pre_lecture_question_section ?? section.preLectureQuestionSection,
      )
        ? ((
            section.pre_lecture_question_section ?? section.preLectureQuestionSection
          ) as RawQuestionSection)
        : null;
      const homeworkQuestions = toRecordArray<RawQuestion>(
        homeworkSection?.questions ?? homeworkSection?.questionsList,
      ).map((question, questionIndex) =>
        createGradebookQuestionMeta(question, 'homework', questionIndex, sectionId, sectionIndex),
      );
      const preLectureQuestions = toRecordArray<RawQuestion>(
        preLectureSection?.questions ?? preLectureSection?.questionsList,
      ).map((question, questionIndex) =>
        createGradebookQuestionMeta(question, 'preLecture', questionIndex, sectionId, sectionIndex),
      );

      return {
        homeworkQuestions,
        id: sectionId,
        index: sectionIndex,
        preLectureQuestions,
      } satisfies GradebookSectionMeta;
    });

    // Mark each question assigned/unassigned per the component's question settings.
    // The settings reference questions by display order across the component's
    // sections, so walk the sections in order with a running index per kind.
    const homeworkSettings = getQuestionSettings(component, 'homework');
    const preLectureSettings = getQuestionSettings(component, 'preLecture');
    const assignedHomeworkIndexes = getAssignedDisplayIndexes(
      homeworkSettings,
      sections.reduce((count, section) => count + section.homeworkQuestions.length, 0),
    );
    const assignedPreLectureIndexes = getAssignedDisplayIndexes(
      preLectureSettings,
      sections.reduce(
        (count, section) => count + section.preLectureQuestions.length,
        0,
      ),
    );
    // A question counts when the settings assign it and it has not been
    // removed from the assignment. Labels number the questions that count, per
    // kind, as students see them: a practice or removed question leaves no gap.
    const markCounted = (assignedIndexes: Set<number>) => {
      let position = 0;
      let counted = 0;
      let practice = 0;
      return (question: GradebookQuestionMeta): GradebookQuestionMeta => {
        const isAssigned = assignedIndexes.has(position) && !question.isRemoved;
        position += 1;
        if (isAssigned) {
          counted += 1;
          return { ...question, isAssigned, label: `Q${counted}` };
        }
        if (question.isRemoved) {
          return { ...question, isAssigned, label: 'Removed question' };
        }
        practice += 1;
        return { ...question, isAssigned, label: `Practice ${practice}` };
      };
    };
    const markHomework = markCounted(assignedHomeworkIndexes);
    const markPreLecture = markCounted(assignedPreLectureIndexes);
    const assignedSections = sections.map((section) => ({
      ...section,
      homeworkQuestions: section.homeworkQuestions.map(markHomework),
      preLectureQuestions: section.preLectureQuestions.map(markPreLecture),
    })) satisfies GradebookSectionMeta[];

    const sumCounted = (questions: GradebookQuestionMeta[]) =>
      questions.reduce(
        (total, question) => (question.isAssigned ? total + question.maxScore : total),
        0,
      );
    const homeworkMaxScore = sumCounted(
      assignedSections.flatMap((section) => section.homeworkQuestions),
    );
    const preLectureMaxScore = sumCounted(
      assignedSections.flatMap((section) => section.preLectureQuestions),
    );

    // Kai Coach participation: the max is the sum of points over currently-open
    // scorable units (matches the backend's coachVisibleMaxScore and the student
    // view). Only counts when the instructor enabled credit.
    const coachSection = getKaiCoachSection(component);
    const coachThreads = coachSection ? normalizeCoachSection(coachSection).threads : [];
    const now = Date.now();
    const participationMaxScore = coachSection ? coachVisibleMaxScore(coachThreads, now) : 0;
    const hasParticipation =
      coachSection !== null &&
      coachSection.scoring?.enabled === true &&
      participationMaxScore > 0;
    // The units an instructor can score by hand: the same open units, with
    // points, that make up the maximum.
    const coachUnits = hasParticipation
      ? coachThreads
          .filter((thread) => isThreadOpen(thread, now) && thread.points > 0)
          .map((thread) => ({
            label:
              thread.sessionLabel && thread.sessionLabel !== thread.label
                ? `${thread.sessionLabel} · ${thread.label}`
                : thread.label,
            points: thread.points,
            uid: thread.scoreUnitUid,
          }))
      : [];

    return {
      componentId,
      componentUid,
      hasHomework: homeworkMaxScore > 0,
      hasParticipation,
      hasPreLecture: preLectureMaxScore > 0,
      homeworkMaxScore,
      isComplete: normalizeBoolean(component.is_complete ?? component.isComplete) ?? false,
      isLive: normalizeBoolean(component.is_live ?? component.isLive) ?? false,
      isSectionDivider,
      isVisibleToStudents: isStudentComponentVisible(component),
      participationMaxScore,
      ...(hasParticipation ? { coachUnits } : {}),
      preLectureMaxScore,
      sections: assignedSections,
      title,
    } satisfies GradebookAssignment;
  });
}

function getSectionQuestions(component: RawComponent, kind: 'homework' | 'preLecture') {
  return toRecordArray<RawSection>(component.sections).flatMap((section) => {
    const questionSection =
      kind === 'preLecture'
        ? section.pre_lecture_question_section ?? section.preLectureQuestionSection
        : section.question_section ?? section.questionSection;

    return isRecord(questionSection)
      ? toRecordArray<RawQuestion>(
          questionSection.questions ?? questionSection.questionsList,
        )
      : [];
  });
}

// The questions that count, by the gradebook's own rule (markCounted): the
// settings assign them, and they have not been removed.
function getAssignedQuestionCount(
  component: RawComponent,
  kind: 'homework' | 'preLecture',
) {
  const questions = getSectionQuestions(component, kind);
  const assignedIndexes = getAssignedDisplayIndexes(
    getQuestionSettings(component, kind),
    questions.length,
  );

  return questions.filter(
    (question, index) =>
      assignedIndexes.has(index) && !normalizeBoolean(question.removed),
  ).length;
}

function isFutureDate(date: Date | null) {
  return Boolean(date && date.getTime() > Date.now());
}

// A visible Kai Coach's deadlines that are still ahead: every open session (or
// activity with its own due date) whose due date is in the future, in due-date
// order. Unopened, closed and undated sessions are left out.
function getCoachDueSoonLines(component: RawComponent, now = Date.now()): CoachDueSoonLine[] {
  const coachSection = getKaiCoachSection(component);
  if (!coachSection || !isStudentComponentVisible(component)) {
    return [];
  }
  const creditEnabled = coachSection.scoring?.enabled === true;
  return coachDueEntries(normalizeCoachSection(coachSection).threads)
    .filter(
      (entry) =>
        (entry.openAtMillis === null || entry.openAtMillis <= now) && entry.dueAtMillis > now,
    )
    .map((entry) => {
      const date = getDateTimeLabel(entry.dueAtMillis);
      return {
        creditEnabled,
        dueAtMillis: entry.dueAtMillis,
        dueLabel: `Due ${date}`,
        entryKey: entry.key,
        kind: 'coachSession',
        rowLabel: `${entry.label} due ${date}`,
        sessionLabel: entry.label,
        threadKey: entry.threadKeys[0],
        units: entry.units,
      } satisfies CoachDueSoonLine;
    });
}

function getDueSoonLines(component: RawComponent): DueSoonLine[] {
  const timeline = getComponentTimeline(component as ContentRawComponent);
  const lines: DueSoonLine[] = [];
  const homeworkQuestionCount = getAssignedQuestionCount(component, 'homework');
  const preLectureQuestionCount = getAssignedQuestionCount(
    component,
    'preLecture',
  );

  if (
    homeworkQuestionCount > 0 &&
    timeline.isHomeworkOpen &&
    isFutureDate(timeline.homeworkEndDate)
  ) {
    lines.push({
      dueAtMillis: timeline.homeworkEndDate!.getTime(),
      dueLabel: `Due ${getDateTimeLabel(timeline.homeworkEndDate!.getTime())}`,
      kind: 'homework',
      questionCount: homeworkQuestionCount,
      typeLabel: 'Homework',
    });
  }

  if (
    preLectureQuestionCount > 0 &&
    timeline.isPreLectureOpen &&
    isFutureDate(timeline.preLectureEndDate)
  ) {
    lines.push({
      dueAtMillis: timeline.preLectureEndDate!.getTime(),
      dueLabel: `Due ${getDateTimeLabel(timeline.preLectureEndDate!.getTime())}`,
      kind: 'preLecture',
      questionCount: preLectureQuestionCount,
      typeLabel: 'Pre-lecture',
    });
  }

  return [...lines, ...getCoachDueSoonLines(component)];
}

// A Kai Coach students have had is closed once every one of its sessions, or
// activities with their own due dates, has passed its due date. One with no due
// date never closes (issue #174).
function getCoachClosedEntries(component: RawComponent, now = Date.now()) {
  const coachSection = getKaiCoachSection(component);
  if (!coachSection || !isStudentComponentVisible(component)) {
    return [];
  }
  const { threads } = normalizeCoachSection(coachSection);
  return threads.length > 0 && threads.every((thread) => isThreadClosed(thread, now))
    ? coachDueEntries(threads)
    : [];
}

// A closed coach's row names the last of its deadlines, as a homework's does.
function getCoachClosedLabel(component: RawComponent) {
  const last = getCoachClosedEntries(component).at(-1);
  return last ? `${last.label} closed ${getDayLabel(new Date(last.dueAtMillis))}` : undefined;
}

// A chapter the retired app closed has no date to give.
function getClosedLabel(
  partName: 'Homework' | 'Pre-lecture',
  questionCount: number,
  isClosed: boolean,
  endDate: Date | null,
) {
  return questionCount > 0 && isClosed && endDate
    ? `${partName} closed ${getDayLabel(endDate)}`
    : undefined;
}

function getComponentMetadataLabels(
  component: RawComponent,
  statusLabel: CourseComponentItem['statusLabel'],
) {
  const timeline = getComponentTimeline(component as ContentRawComponent);
  const homeworkQuestionCount = getAssignedQuestionCount(component, 'homework');
  const preLectureQuestionCount = getAssignedQuestionCount(
    component,
    'preLecture',
  );

  // Once the chapter is Closed, the two due slots say when each part closed.
  if (statusLabel === 'Closed') {
    return {
      dueLabel:
        getClosedLabel(
          'Homework',
          homeworkQuestionCount,
          timeline.isHomeworkClosed,
          timeline.homeworkEndDate,
        ) ?? getCoachClosedLabel(component),
      preLectureLabel: getClosedLabel(
        'Pre-lecture',
        preLectureQuestionCount,
        timeline.isPreLectureClosed,
        timeline.preLectureEndDate,
      ),
      visibilityLabel: undefined,
    };
  }

  // A Kai Coach names its next deadline, in the same slot as "Homework due".
  const nextCoachLine = statusLabel === 'Live' ? getCoachDueSoonLines(component)[0] : undefined;
  const dueLabel =
    statusLabel === 'Live' &&
    homeworkQuestionCount > 0 &&
    timeline.isHomeworkOpen &&
    isFutureDate(timeline.homeworkEndDate)
      ? `Homework due ${getDateTimeLabel(timeline.homeworkEndDate!.getTime())}`
      : nextCoachLine?.rowLabel;
  const preLectureLabel =
    statusLabel === 'Live' &&
    preLectureQuestionCount > 0 &&
    timeline.isPreLectureOpen &&
    isFutureDate(timeline.preLectureEndDate)
      ? `Pre-lecture due ${getDateTimeLabel(
          timeline.preLectureEndDate!.getTime(),
        )}`
      : undefined;
  // A student sees such a row only in the student view, with all content shown.
  const visibilityLabel =
    (statusLabel === 'Draft' || statusLabel === 'Scheduled') &&
    isFutureDate(timeline.contentVisibleDate)
      ? `Visible to students on ${getDateTimeLabel(
          timeline.contentVisibleDate!.getTime(),
        )}`
      : undefined;

  return {
    dueLabel,
    preLectureLabel,
    visibilityLabel,
  };
}

function isStudentComponentVisible(component: RawComponent) {
  const timeline = getComponentTimeline(component as ContentRawComponent);

  return (
    timeline.isContentVisible ||
    timeline.isHomeworkVisible ||
    timeline.isHomeworkClosed ||
    timeline.isPreLectureVisible ||
    timeline.isPreLectureClosed
  );
}

// A student sees a divider only over a chapter they can see: one heading the
// course's first chapter too, which the instructor sees (issue #174).
function trimDividerItems(items: ComponentListItem[]) {
  return items.filter(
    (item, index) => item.kind !== 'divider' || items[index + 1]?.kind === 'component',
  );
}

// Closed once a part with questions has closed and none is left to answer. A
// part with no questions that count has no say, and neither does one that never
// opened and has no date to open on: a chapter the retired app closed can hold
// pre-lecture questions nobody ever used. A chapter with no questions has only
// its own dates and flags to go by.
function isChapterClosed(component: RawComponent, timeline: ComponentTimeline) {
  const hasHomework = getAssignedQuestionCount(component, 'homework') > 0;
  const hasPreLecture = getAssignedQuestionCount(component, 'preLecture') > 0;

  if (!hasHomework && !hasPreLecture) {
    return timeline.isHomeworkClosed || timeline.isPreLectureClosed;
  }

  const homeworkIsLeft =
    hasHomework &&
    !timeline.isHomeworkClosed &&
    (timeline.isHomeworkOpen || timeline.homeworkStartDate !== null);
  const preLectureIsLeft =
    hasPreLecture &&
    !timeline.isPreLectureClosed &&
    (timeline.isPreLectureOpen || timeline.preLectureStartDate !== null);

  return (
    !homeworkIsLeft &&
    !preLectureIsLeft &&
    ((hasHomework && timeline.isHomeworkClosed) ||
      (hasPreLecture && timeline.isPreLectureClosed))
  );
}

function getComponentStatusLabel(
  component: RawComponent,
): CourseComponentItem['statusLabel'] {
  const timeline = getComponentTimeline(component as ContentRawComponent);

  if (isChapterClosed(component, timeline) || getCoachClosedEntries(component).length > 0) {
    return 'Closed';
  }

  // A closed part stays visible to students (isStudentComponentVisible), even
  // with the rest of the chapter yet to open.
  if (
    timeline.isContentVisible ||
    timeline.isHomeworkOpen ||
    timeline.isPreLectureOpen ||
    timeline.isHomeworkClosed ||
    timeline.isPreLectureClosed
  ) {
    return 'Live';
  }

  // A student sees a row that is not live only in the student view, with all
  // content shown, and it reads as the instructor's does (issue #174).
  return timeline.contentVisibleDate ||
    timeline.homeworkStartDate ||
    timeline.preLectureStartDate
    ? 'Scheduled'
    : 'Draft';
}

export function normalizeCourseResponse(
  course: RawCourse,
  {
    courseId,
    currentUser,
    includeHiddenComponentsForStudent = false,
  }: NormalizeCourseOptions,
): CourseHomepage {
  const id =
    normalizeString(course.id) ??
    normalizeString(course.uid) ??
    normalizeString(courseId) ??
    'course';
  const title = normalizeString(course.name) ?? 'Untitled course';
  const termFromStart = deriveTermLabel(
    getDateMessage(course.start_date ?? course.startDate),
  );
  const termLabel =
    termFromStart !== 'Term TBD'
      ? termFromStart
      : deriveTermLabel(getDateMessage(course.end_date ?? course.endDate));
  const userRole = deriveUserRole(course, currentUser);
  const isInstructorView = userRole !== 'student';
  const orderedComponents = orderCourseComponents(course);
  const courseSettings = getCourseSettings(course);
  const assistantInfo = getAssistantInfo(course);
  const assistantIds = toStringArray(course.assistant_ids ?? course.assistantIds);
  const secondaryInstructorIds = toStringArray(
    course.secondary_instructor_ids ?? course.secondaryInstructorIds,
  );
  const gradebookAssignments = getGradebookAssignments(orderedComponents);
  const customComponentOrder =
    normalizeBoolean(
      course.custom_component_order ?? course.customComponentOrder,
    ) ?? false;
  const orderedComponentUids = orderedComponents.map((component, index) =>
    normalizeString(component.uid) ??
    normalizeString(component.id) ??
    `component-${index + 1}`,
  );
  // The course's ComponentUids list is what the backend deletes against
  // (DeleteComponent removes the entry equal to component_id). New components are
  // stored there by uid, but the list can defensively hold a component's id too,
  // so the delete key must be whichever of the component's uid/id actually
  // appears in it — not an unconditional id-first value (which silently no-ops).
  const componentUidSet = new Set(
    toStringArray(course.component_uids ?? course.componentUids),
  );

  const componentItems = orderedComponents.map((component, index) => {
      const componentId =
        normalizeString(component.uid) ??
        normalizeString(component.id) ??
        `component-${index + 1}`;
      const rawComponentUid = normalizeString(component.uid);
      const rawComponentRealId = normalizeString(component.id);
      const rawComponentId =
        (rawComponentUid && componentUidSet.has(rawComponentUid)
          ? rawComponentUid
          : undefined) ??
        (rawComponentRealId && componentUidSet.has(rawComponentRealId)
          ? rawComponentRealId
          : undefined) ??
        componentId;
      const titleLabel = normalizeString(component.title) ?? 'Untitled component';
      const isDivider =
        normalizeBoolean(
          component.is_section_divider ?? component.isSectionDivider,
        ) ?? false;

      if (isDivider) {
        return {
          componentId: rawComponentId,
          ...(rawComponentUid ? { componentUid: rawComponentUid } : {}),
          id: componentId,
          kind: 'divider',
          title: titleLabel,
        } satisfies SectionDividerItem;
      }

      const isLive = normalizeBoolean(component.is_live ?? component.isLive);
      const isComplete = normalizeBoolean(
        component.is_complete ?? component.isComplete,
      );
      const isPreLectureLive = normalizeBoolean(
        component.is_pre_lecture_live ?? component.isPreLectureLive,
      );
      const isPreLectureComplete = normalizeBoolean(
        component.is_pre_lecture_complete ?? component.isPreLectureComplete,
      );
      const isVisibleToStudent = isStudentComponentVisible(component);
      const isHiddenFromStudents = userRole === 'student' && !isVisibleToStudent;

      if (isHiddenFromStudents && !includeHiddenComponentsForStudent) {
        return null;
      }
      const statusLabel = getComponentStatusLabel(component);
      const metadataLabels = getComponentMetadataLabels(component, statusLabel);

      return {
        componentId: rawComponentId,
        ...(rawComponentUid ? { componentUid: rawComponentUid } : {}),
        dueLabel: metadataLabels.dueLabel,
        dueSoonLines: getDueSoonLines(component),
        href: `/app/courses/${courseId}/${componentId}`,
        id: componentId,
        isComplete,
        isLive,
        isPreLectureComplete,
        isPreLectureLive,
        kind: 'component',
        preLectureLabel: metadataLabels.preLectureLabel,
        rawComponent: component,
        statusLabel,
        title: titleLabel,
        // In the student view, with all content shown: when students will see
        // a scheduled row, or that they do not.
        visibilityLabel: isHiddenFromStudents
          ? (metadataLabels.visibilityLabel ?? 'Hidden from students')
          : metadataLabels.visibilityLabel,
      } satisfies CourseComponentItem;
    });

  // How many components visibility settings hide from students right now —
  // student view uses this to offer the "show all content" reveal.
  const hiddenComponentCount =
    userRole === 'student'
      ? orderedComponents.filter(
          (component) =>
            !(
              normalizeBoolean(
                component.is_section_divider ?? component.isSectionDivider,
              ) ?? false
            ) && !isStudentComponentVisible(component),
        ).length
      : 0;

  return {
    ...(assistantInfo ? { assistantInfo } : {}),
    assistantIds,
    assistants: normalizeStaffMembers(
      assistantIds,
      toUserArray(course.assistants),
    ),
    componentUids: orderedComponentUids,
    ...(userRole === 'student' ? { hiddenComponentCount } : {}),
    components:
      userRole === 'student'
        ? trimDividerItems(
            componentItems.filter(
              (item): item is NonNullable<(typeof componentItems)[number]> =>
                item !== null,
            ),
          )
        : componentItems.filter(
            (item): item is NonNullable<(typeof componentItems)[number]> =>
              item !== null,
          ),
    gradebookAssignments,
    coursePrelectureQuestionSettings: getCoursePrelectureQuestionSettings(course),
    courseQuestionSettings: getCourseQuestionSettings(course),
    courseSettings,
    customComponentOrder,
    id,
    ...(normalizeStaffMember(course.instructor)
      ? { instructor: normalizeStaffMember(course.instructor)! }
      : {}),
    ...(normalizeString(course.instructor_id ?? course.instructorId)
      ? { instructorId: normalizeString(course.instructor_id ?? course.instructorId)! }
      : {}),
    instructorName: getUserDisplayName(course.instructor) ?? 'Course staff',
    isComplete: normalizeBoolean(course.is_complete ?? course.isComplete),
    isInstructorView,
    isLive: normalizeBoolean(course.is_live ?? course.isLive),
    ...(normalizeString(course.parent_subject ?? course.parentSubject)
      ? { parentSubject: normalizeString(course.parent_subject ?? course.parentSubject)! }
      : {}),
    secondaryInstructorIds,
    secondaryInstructors: normalizeStaffMembers(
      secondaryInstructorIds,
      toUserArray(
        course.secondary_instructors ?? course.secondaryInstructors,
      ),
    ),
    studentCount: getStudentCount(course),
    termLabel,
    title,
    userRole,
  };
}

export function unwrapCourseResponse(
  response:
    | RawCourse
    | RawGetCoursePayload
    | LegacyKuduResponseEnvelope<RawCourse | RawGetCoursePayload>
    | null
    | undefined,
): RawCourse | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapCourseResponse(
      response.Response as RawCourse | RawGetCoursePayload,
    );
  }

  if (isRecord(response) && 'course' in response) {
    return isRecord(response.course) ? (response.course as RawCourse) : null;
  }

  return isRecord(response) ? (response as RawCourse) : null;
}

export function unwrapComponentResponse(
  response:
    | RawComponent
    | RawGetComponentPayload
    | LegacyKuduResponseEnvelope<RawComponent | RawGetComponentPayload>
    | null
    | undefined,
): RawComponent | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapComponentResponse(
      response.Response as RawComponent | RawGetComponentPayload,
    );
  }

  if (isRecord(response) && 'component' in response) {
    return isRecord(response.component)
      ? (response.component as RawComponent)
      : null;
  }

  return isRecord(response) ? (response as RawComponent) : null;
}

export function useCourse(
  courseId: string | undefined,
): UseQueryResult<CourseHomepage, ApiError> {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, loading, user } = useAuth();
  // Student view's display-only reveal of hidden components. Part of the
  // query key so toggling recomputes the normalized course (and toggling back
  // is served from cache).
  const studentView = useStudentView();
  const includeHiddenComponentsForStudent =
    studentView.active && studentView.showHiddenContent;

  return useQuery<CourseHomepage, ApiError>({
    enabled: !loading && Boolean(user) && Boolean(courseId),
    queryFn: async () => {
      if (!user || !courseId) {
        throw createDataError('Course ID is required to load the course homepage.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        // TODO: Replace the temporary raw response types here with protobuf-ts
        // generated types once that pipeline is wired into frontend-next.
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const response = await client.invokeKuduMethod<
        RawGetCoursePayload | LegacyKuduResponseEnvelope<RawGetCoursePayload>,
        GetCourseRequest
      >('GetCourse', {
        course_id: courseId,
        full_response: true,
      });
      const course = unwrapCourseResponse(response);

      if (!course) {
        throw createDataError('Unable to load this course right now.', 404);
      }

      const componentIds = toStringArray(
        course.component_uids ?? course.componentUids,
      );

      const resolvedComponents =
        componentIds.length > 0
          ? (
              await Promise.all(
                componentIds.map(async (componentId) => {
                  try {
                    const componentResponse = await client.invokeKuduMethod<
                      | RawGetComponentPayload
                      | LegacyKuduResponseEnvelope<RawGetComponentPayload>,
                      GetComponentRequest
                    >('GetComponent', {
                      component_id: componentId,
                      course_id: courseId,
                    });
                    const component = unwrapComponentResponse(componentResponse);

                    if (!component) {
                      console.error(
                        'Failed to load component for course homepage',
                        {
                          componentId,
                          courseId,
                          reason: 'Missing component payload',
                        },
                      );
                      return null;
                    }

                    return component;
                  } catch (error) {
                    console.error('Failed to load component for course homepage', {
                      componentId,
                      courseId,
                      error,
                    });
                    return null;
                  }
                }),
              )
            ).filter((component): component is RawComponent => component !== null)
          : toComponentArray(course.components);

      return normalizeCourseResponse(
        {
          ...course,
          components: resolvedComponents,
        },
        {
          courseId,
          currentUser: user,
          includeHiddenComponentsForStudent,
        },
      );
    },
    queryKey: courseId
      ? includeHiddenComponentsForStudent
        ? [...courseQueryKey(courseId), 'student-view-reveal']
        : courseQueryKey(courseId)
      : ['course', 'missing'],
    retry: false,
  });
}
